import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AppHeader } from "./AppHeader";
import { BackButton } from "./BackButton";
import {
  APP_NAME, LEGAL_ROUTES, LEGAL_LAST_UPDATED, LEGAL_DOC_VERSION,
  APP_PUBLIC_URL, buildCanonicalUrl, IS_PREVIEW,
} from "../lib/legal";
import { LegalPreviewBanner } from "./LegalPreviewBanner";

// Shared shell for the four public legal pages (Privacy / Terms / LegalMentions
// / DeleteAccountPublic). Provides:
//   - AppHeader (same logo as the rest of the app)
//   - Centred reading column (max-width ~720)
//   - Common footer nav (Confidentialité · Conditions · Mentions · Suppression · Retour)
//   - Document metadata (last update + version) at the bottom
//   - <title> + <meta> updates via document.head (SEO)
//
// Consumers pass:
//   title       — H1 shown at top + <title> tag
//   description — short meta description
//   pathname    — /privacy | /terms | /legal | /delete-account (for canonical + active state)
//   showPreviewBanner — true only on /legal while placeholders remain
export function LegalLayout({ title, description, pathname, showPreviewBanner = false, children }) {
  const location = useLocation();
  const activePath = pathname || location.pathname;

  // SEO — set document title + meta description + canonical + robots on mount.
  useEffect(() => {
    const prevTitle = document.title;
    const desc = description || `${title} — ${APP_NAME}.`;
    const canonical = buildCanonicalUrl(activePath);

    document.title = `${title} | ${APP_NAME}`;

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    let metaDescCreated = false;
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
      metaDescCreated = true;
    }
    const prevDesc = metaDesc.getAttribute("content");
    metaDesc.setAttribute("content", desc);

    // Canonical
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    let canonicalCreated = false;
    if (!linkCanonical) {
      linkCanonical = document.createElement("link");
      linkCanonical.setAttribute("rel", "canonical");
      document.head.appendChild(linkCanonical);
      canonicalCreated = true;
    }
    const prevCanonical = linkCanonical.getAttribute("href");
    linkCanonical.setAttribute("href", canonical);

    // Robots — index/follow public legal pages
    let robots = document.querySelector('meta[name="robots"]');
    let robotsCreated = false;
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
      robotsCreated = true;
    }
    const prevRobots = robots.getAttribute("content");
    robots.setAttribute("content", "index,follow");

    // Open Graph minimal
    const ogTags = [
      ["og:title", `${title} | ${APP_NAME}`],
      ["og:description", desc],
      ["og:url", canonical],
      ["og:type", "website"],
      ["og:site_name", APP_NAME],
      ["og:locale", "fr_FR"],
    ];
    const ogEls = ogTags.map(([prop, val]) => {
      let el = document.querySelector(`meta[property="${prop}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", prop);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", val);
      return { el, prev, created };
    });

    // Language on <html>
    const prevLang = document.documentElement.getAttribute("lang");
    document.documentElement.setAttribute("lang", "fr");

    return () => {
      document.title = prevTitle;
      if (metaDescCreated) metaDesc.remove();
      else if (prevDesc !== null) metaDesc.setAttribute("content", prevDesc);
      if (canonicalCreated) linkCanonical.remove();
      else if (prevCanonical !== null) linkCanonical.setAttribute("href", prevCanonical);
      if (robotsCreated) robots.remove();
      else if (prevRobots !== null) robots.setAttribute("content", prevRobots);
      ogEls.forEach(({ el, prev, created }) => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute("content", prev);
      });
      if (prevLang) document.documentElement.setAttribute("lang", prevLang);
    };
  }, [title, description, activePath]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--mf-bg)", display: "flex", flexDirection: "column" }}>
      <AppHeader background="surface" clickable={false} />

      <main
        role="main"
        style={{
          flex: 1,
          padding: "24px 20px 40px",
          maxWidth: 760,
          margin: "0 auto",
          width: "100%",
          boxSizing: "border-box",
        }}
        data-testid={`legal-page-${activePath.replace(/^\/+/, "") || "root"}`}
      >
        {showPreviewBanner && <LegalPreviewBanner />}

        <BackButton testId={`legal-back-${activePath.replace(/^\/+/, "") || "root"}`} />

        <h1 style={{
          margin: "8px 0 8px",
          fontSize: "clamp(28px, 4.2vw, 36px)",
          fontWeight: 800,
          color: "var(--mf-text)",
          letterSpacing: -0.4,
          lineHeight: 1.2,
        }}>
          {title}
        </h1>
        <p style={{
          margin: "0 0 28px",
          fontSize: 13,
          color: "var(--mf-text-muted)",
        }}>
          Dernière mise à jour : {LEGAL_LAST_UPDATED} · Version {LEGAL_DOC_VERSION}
          {IS_PREVIEW && (
            <>
              {" · "}
              <span style={{ color: "var(--mf-text-muted)", fontStyle: "italic" }}>
                Environnement Preview — URL provisoire
              </span>
            </>
          )}
        </p>

        <div className="mf-legal-content" style={{
          fontSize: 15,
          lineHeight: 1.7,
          color: "var(--mf-text)",
        }}>
          {children}
        </div>
      </main>

      <LegalFooter activePath={activePath} />
    </div>
  );
}

function LegalFooter({ activePath }) {
  const items = [
    { to: LEGAL_ROUTES.support, label: "Assistance", testid: "legal-nav-support" },
    { to: LEGAL_ROUTES.privacy, label: "Confidentialité", testid: "legal-nav-privacy" },
    { to: LEGAL_ROUTES.terms, label: "Conditions", testid: "legal-nav-terms" },
    { to: LEGAL_ROUTES.legal, label: "Mentions légales", testid: "legal-nav-legal" },
    { to: LEGAL_ROUTES.deleteAccount, label: "Suppression du compte", testid: "legal-nav-delete" },
  ];

  return (
    <footer
      role="contentinfo"
      style={{
        padding: "20px 20px calc(20px + env(safe-area-inset-bottom, 0px))",
        borderTop: "1px solid var(--mf-divider)",
        background: "var(--mf-bg-card)",
      }}
      data-testid="legal-footer"
    >
      <nav
        aria-label="Navigation légale"
        style={{
          maxWidth: 760, margin: "0 auto",
          display: "flex", flexWrap: "wrap", gap: "6px 14px",
          justifyContent: "center", alignItems: "center",
        }}
      >
        {items.map((it, i) => {
          const active = activePath === it.to;
          return (
            <React.Fragment key={it.to}>
              <Link
                to={it.to}
                data-testid={it.testid}
                aria-current={active ? "page" : undefined}
                style={{
                  color: active ? "var(--mf-brand)" : "var(--mf-text-secondary)",
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  textDecoration: "none",
                  padding: "4px 2px",
                }}
              >
                {it.label}
              </Link>
              {i < items.length - 1 && (
                <span aria-hidden="true" style={{ color: "var(--mf-text-muted)", fontSize: 12 }}>·</span>
              )}
            </React.Fragment>
          );
        })}
        <span aria-hidden="true" style={{ color: "var(--mf-text-muted)", fontSize: 12 }}>·</span>
        <Link
          to={LEGAL_ROUTES.login}
          data-testid="legal-nav-back-login"
          style={{
            color: "var(--mf-text-secondary)",
            fontSize: 13,
            fontWeight: 500,
            textDecoration: "none",
            padding: "4px 2px",
          }}
        >
          Retour à la connexion
        </Link>
      </nav>
      <p style={{
        margin: "12px 0 0", textAlign: "center",
        color: "var(--mf-text-muted)", fontSize: 11,
      }}>
        © {new Date().getFullYear()} {APP_NAME}
      </p>
    </footer>
  );
}
